import React, { useEffect, useState } from "react";
import Layout from "../../components/layouts/Layout";
import BreadcrumbBlock from "../../components/BreadcrumbBlock/BreadcrumbBlock";
import { Link, useNavigate } from "react-router-dom";
import { Card, Row, Col, List, Button, Spin, Flex, Badge } from "antd";
import {
  Home2,
  MessageText,
  Timer,
  ConvertCard,
  InfoCircle,
} from "iconsax-react";
import { RobotFilled } from "@ant-design/icons";
import { mixGetAi } from "../../services/apiAi";
import dayjs from "dayjs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./TicketDashboard.scss";

interface DailyVolume {
  date: string;
  bot: number;
  human: number;
  total: number;
}

interface TopReason {
  reason: string;
  count: number;
}

interface MetricsData {
  total: number;
  bot_resolved: number;
  bot_pct: number;
  escalated: number;
  escalated_pct: number;
  avg_first_response_sec: number; // in seconds
  volume_daily: DailyVolume[];
  top_reasons: TopReason[];
}

interface WaitingConversation {
  conversation_id: string;
  ticket_no: string;
  customer_name: string;
  last_message_at: string;
}

const TicketDashboard: React.FC = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const navigate = useNavigate();

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [waitingLoading, setWaitingLoading] = useState(false);
  const [metrics, setMetrics] = useState<MetricsData>();
  const [waitingConversations, setWaitingConversations] = useState<
    WaitingConversation[]
  >([]);

  // Authentication check - admin only
  useEffect(() => {
    if (!user || user?.role !== 1) {
      navigate("/");
    }
  }, [user, navigate]);

  const fetchMetrics = async () => {
    try {
      setMetricsLoading(true);
      const res = await mixGetAi("/admin/metrics/overview", {});
      if (res) {
        // Merge with mock to ensure we don't have partial/empty datasets
        setMetrics({
          total: res.total,
          bot_resolved: res.bot_resolved,
          bot_pct: res.bot_pct,
          escalated: res.escalated,
          escalated_pct: res.escalated_pct,
          avg_first_response_sec: res.avg_first_response_sec,
          volume_daily: res.volume_daily,
          top_reasons: res.top_reasons,
        });
      } else {
        setMetrics(undefined);
      }
    } catch (error) {
      setMetrics(undefined);
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchWaitingConversations = async () => {
    try {
      setWaitingLoading(true);
      const res = await mixGetAi("/admin/conversations", { waiting: "true" });
      if (res && Array.isArray(res)) {
        setWaitingConversations(res);
      } else {
        setWaitingConversations([]);
      }
    } catch (error) {
      console.error("Error fetching waiting conversations:", error);
      setWaitingConversations([]);
    } finally {
      setWaitingLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    fetchWaitingConversations();
  }, []);

  const getWaitingTimeStr = (lastMsgAt: string) => {
    if (!lastMsgAt) return "waiting > 12h";
    const diffHrs = dayjs().diff(dayjs(lastMsgAt), "hour");
    if (diffHrs < 1) return "waiting < 1h";
    return `waiting ${diffHrs}h`;
  };

  const handleWaitingClick = (convId: string) => {
    navigate(`/ticket-detail/${convId}`);
  };

  const formatDateLabel = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return dayjs(dateStr).format("MMM DD");
    }
    return dateStr;
  };

  return (
    <Layout contentClassName="ticket-dashboard-page">
      <BreadcrumbBlock
        items={[
          {
            title: (
              <Flex align="center" gap={16}>
                <Home2 variant="Bulk" />
                <Link to="/">CMD Tech</Link>
              </Flex>
            ),
          },
          { title: "Store Management" },
          { title: "Ticket Dashboard" },
        ]}
        title="Ticket Dashboard"
        description=""
      />

      <div className="ticket-dashboard-container">
        {/* KPI Cards Row */}
        <Spin spinning={metricsLoading}>
          <Row gutter={[24, 24]} className="kpi-cards-row">
            <Col xs={24} md={8}>
              <Card className="kpi-card total-tickets-card" bordered={false}>
                <div className="kpi-label">Total Tickets</div>
                <div className="kpi-value-row">
                  <span className="kpi-number">
                    {metrics?.total?.toLocaleString()}
                  </span>
                  <MessageText className="kpi-icon" size={24} variant="Bulk" />
                </div>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="kpi-card bot-resolved-card" bordered={false}>
                <div className="kpi-label">Bot Resolved</div>
                <div className="kpi-value-row">
                  <span className="kpi-number">
                    {metrics?.bot_resolved?.toLocaleString()}
                  </span>
                  <span className="kpi-percentage success-badge">
                    {metrics?.bot_pct}%
                  </span>
                  <RobotFilled
                    className="kpi-icon icon-antd"
                    style={{ fontSize: 24 }}
                  />
                </div>
              </Card>
            </Col>

            <Col xs={24} md={8}>
              <Card className="kpi-card escalated-card" bordered={false}>
                <div className="kpi-label">Escalated</div>
                <div className="kpi-value-row">
                  <span className="kpi-number">
                    {metrics?.escalated?.toLocaleString()}
                  </span>
                  <span className="kpi-percentage warning-badge">
                    {metrics?.escalated_pct}%
                  </span>
                  <ConvertCard className="kpi-icon" size={24} variant="Bulk" />
                </div>
              </Card>
            </Col>
          </Row>
        </Spin>

        {/* Volume Chart */}
        <Row gutter={[24, 24]} className="chart-row">
          <Col span={24}>
            <Card
              className="dashboard-chart-card"
              title="Ticket volume (daily)"
              bordered={false}
            >
              {metricsLoading ? (
                <div className="chart-loader">
                  <Spin size="large" />
                </div>
              ) : (
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart
                      data={metrics?.volume_daily}
                      margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#1f1f23"
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        stroke="#808191"
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={formatDateLabel}
                      />
                      <YAxis stroke="#808191" fontSize={12} tickLine={false} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "#131316",
                          borderColor: "#1f1f23",
                          borderRadius: "8px",
                          color: "#ffffff",
                        }}
                        itemStyle={{ color: "#ffffff" }}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="circle"
                      />
                      <Bar
                        name="Bot resolved"
                        dataKey="bot"
                        stackId="a"
                        fill="#1890ff"
                        opacity={0.8}
                        radius={[0, 0, 0, 0]}
                      />
                      <Bar
                        name="Human handoff"
                        dataKey="human"
                        stackId="a"
                        fill="#ff4d4f"
                        opacity={0.85}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        {/* Bottom Details Grid */}
        <Row gutter={[24, 24]} className="details-grid-row">
          <Col xs={24} lg={12}>
            <Card
              className="dashboard-list-card needs-attention-card"
              title="Needs attention"
              bordered={false}
            >
              <Spin spinning={waitingLoading}>
                <div className="list-wrapper">
                  <List
                    dataSource={waitingConversations}
                    locale={{ emptyText: "No tickets need attention" }}
                    renderItem={(item) => {
                      const waitingStr = getWaitingTimeStr(
                        item.last_message_at,
                      );
                      // Determine waiting badge style
                      const isOver24 =
                        waitingStr.includes("24h") ||
                        dayjs().diff(dayjs(item.last_message_at), "hour") >= 24;
                      return (
                        <List.Item
                          className="waiting-ticket-item"
                          onClick={() =>
                            handleWaitingClick(item.conversation_id)
                          }
                        >
                          <Flex
                            align="center"
                            justify="space-between"
                            style={{ width: "100%" }}
                          >
                            <div className="ticket-meta">
                              <span className="owner-name">
                                {item.customer_name}
                              </span>
                              <span className="ticket-hash">
                                #TK-{item.ticket_no}
                              </span>
                            </div>
                            <span
                              className={`waiting-badge ${isOver24 ? "danger" : "warning"}`}
                            >
                              {waitingStr}
                            </span>
                          </Flex>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              </Spin>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              className="dashboard-list-card top-reasons-card"
              title="Top escalation reasons"
              bordered={false}
            >
              <Spin spinning={metricsLoading}>
                <div className="list-wrapper">
                  <List
                    dataSource={metrics?.top_reasons}
                    locale={{ emptyText: "No escalation data available" }}
                    renderItem={(item, index) => (
                      <List.Item className="reason-item">
                        <Flex
                          align="center"
                          justify="space-between"
                          style={{ width: "100%" }}
                        >
                          <span className="reason-name">
                            {index + 1}. {item.reason}
                          </span>
                          <span className="reason-count">{item.count}</span>
                        </Flex>
                      </List.Item>
                    )}
                  />
                </div>
              </Spin>
            </Card>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default TicketDashboard;

